import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Quote } from "lucide-react";
import BottleSilhouette from "../components/BottleSilhouette";
import DnaRadar from "../components/DnaRadar";
import { api } from "../lib/api";
import { CATEGORY_COLORS, getCategoryColor } from "../lib/constants";

export default function FlightDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [flight, setFlight] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setFlight(null);
    if (id === "custom") {
      const ids = searchParams.get("ids");
      if (!ids) { setLoading(false); return; }
      api.get(`/flights-custom?ids=${ids}`).then((r) => { setFlight(r.data); setLoading(false); });
    } else {
      api.get(`/flights/${id}`).then((r) => { setFlight(r.data); setLoading(false); }).catch(() => setLoading(false));
    }
  }, [id, searchParams]);

  if (loading) return <div className="px-6 py-32 text-center text-white/50">Pouring the flight...</div>;
  if (!flight) return (
    <div className="px-6 py-32 text-center text-white/50">
      <p className="font-display text-3xl mb-3">No flight found.</p>
      <Link to="/gin/flights" className="text-[#C17F59] uppercase tracking-[0.25em] text-xs">Back to all flights</Link>
    </div>
  );

  const color = CATEGORY_COLORS[flight.color_key] || CATEGORY_COLORS.classic;
  const radarItems = flight.gins.map((g) => ({
    name: g.name, dna: g.dna, color: getCategoryColor(g.category), category: g.category,
  }));

  return (
    <div className="pb-24" data-testid={`flight-detail-${flight.id}`}>
      {/* Back nav */}
      <div className="px-5 md:px-12 max-w-6xl mx-auto pt-4">
        <Link to="/gin/flights" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/50 hover:text-[#C17F59] transition-colors" data-testid="back-flights">
          <ArrowLeft size={14} /> All flights
        </Link>
      </div>

      {/* HERO — editorial cover */}
      <section className="px-5 md:px-12 max-w-6xl mx-auto pt-8 pb-12 grain relative">
        <div className="absolute inset-0 pointer-events-none opacity-30"
             style={{ background: `radial-gradient(ellipse at top right, ${color}33, transparent 60%)` }} />
        <div className="relative">
          <p className="overline mb-3" style={{ color }}>{flight.kicker}</p>
          <h1 className="font-display text-5xl sm:text-7xl lg:text-8xl tracking-tight leading-[0.92] mb-5 max-w-4xl">
            {flight.name}.
          </h1>
          <p className="font-display text-xl md:text-2xl italic text-white/75 max-w-3xl leading-snug">"{flight.blurb}"</p>

          <div className="mt-8 flex flex-wrap items-center gap-x-12 gap-y-3 text-sm">
            <div><span className="overline text-white/40">Pours</span><div className="font-display text-2xl">{flight.pours}</div></div>
            <div><span className="overline text-white/40">Flight Price</span><div className="font-display text-2xl">₹{flight.price}</div></div>
            <div><span className="overline text-white/40">Curated By</span><div className="font-hand text-3xl text-[#D89A74]">{flight.bartender}</div></div>
          </div>
        </div>
      </section>

      {/* BARTENDER INTRO */}
      <section className="px-5 md:px-12 max-w-6xl mx-auto py-10 border-t border-white/5" data-testid="flight-intro">
        <div className="flex items-start gap-5 max-w-3xl">
          <Quote size={32} className="text-[#C17F59] shrink-0 mt-1 -scale-x-100" />
          <p className="font-display text-2xl md:text-3xl leading-snug italic">{flight.bartender_intro}</p>
        </div>
        <p className="font-hand text-2xl text-[#D89A74] mt-4 ml-12 -rotate-1">— {flight.bartender}, behind the bar.</p>
      </section>

      {/* JOURNEY — 3 pours like a tasting notebook */}
      <section className="px-5 md:px-12 max-w-6xl mx-auto py-10 border-t border-white/5 space-y-6" data-testid="flight-journey">
        <div className="flex items-end justify-between mb-4">
          <h2 className="font-display text-4xl">The Journey</h2>
          <p className="overline text-white/40">Drink in order</p>
        </div>
        {flight.journey.map((step, i) => {
          const gin = flight.gins.find((g) => g.id === step.gin_id);
          if (!gin) return null;
          const ginColor = getCategoryColor(gin.category);
          return (
            <motion.article
              key={step.gin_id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.15, duration: 0.6 }}
              className="grid grid-cols-12 gap-4 md:gap-8 bg-[#15171A] border border-white/8 p-5 md:p-7 relative overflow-hidden"
              data-testid={`journey-step-${i}`}
            >
              {/* Big numeral */}
              <div className="col-span-2 md:col-span-1 flex flex-col items-start">
                <span className="font-display text-6xl md:text-7xl leading-none" style={{ color: ginColor + "55" }}>0{i + 1}</span>
              </div>

              {/* Bottle */}
              <div className="col-span-10 md:col-span-3 flex items-center justify-center md:border-r md:border-white/5 md:pr-5">
                <Link to={`/gin/${gin.id}`} className="group relative" data-testid={`journey-bottle-${gin.id}`}>
                  <div className="absolute inset-0 -m-4 opacity-30 group-hover:opacity-60 transition-opacity"
                       style={{ background: `radial-gradient(circle, ${ginColor}33, transparent 70%)` }} />
                  <BottleSilhouette category={gin.category} size={90} />
                </Link>
              </div>

              {/* Copy */}
              <div className="col-span-12 md:col-span-8 space-y-3">
                <div className="flex items-center gap-3">
                  <span className="overline" style={{ color: ginColor }}>{step.role}</span>
                  <span className="h-px flex-1 max-w-20" style={{ background: ginColor + "44" }} />
                  <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">{gin.country} · {gin.abv}%</span>
                </div>
                <Link to={`/gin/${gin.id}`} className="block group">
                  <h3 className="font-display text-3xl md:text-4xl group-hover:text-[#C17F59] transition-colors">{gin.name}</h3>
                </Link>
                <p className="font-display text-lg md:text-xl italic text-white/85 leading-snug">"{step.copy}"</p>
                <div className="flex flex-wrap gap-1.5 pt-2">
                  {gin.moods?.slice(0, 3).map((m) => (
                    <span key={m} className="text-[10px] uppercase tracking-[0.18em] text-white/50 border border-white/10 px-2 py-0.5">{m}</span>
                  ))}
                </div>
              </div>
            </motion.article>
          );
        })}
      </section>

      {/* DNA OVERLAY */}
      <section className="px-5 md:px-12 max-w-6xl mx-auto py-12 border-t border-white/5" data-testid="flight-dna-overlay">
        <p className="overline text-[#C17F59] mb-2">DNA Read</p>
        <h2 className="font-display text-4xl mb-2">How they layer.</h2>
        <p className="text-white/60 max-w-2xl mb-8 text-sm">Three flavor maps, on top of each other. The closer the polygons, the more they belong together.</p>
        <div className="bg-[#15171A] border border-white/8 p-6">
          <DnaRadar items={radarItems} height={400} showLegend />
          <div className="flex flex-wrap gap-4 justify-center pt-3">
            {radarItems.map((it) => (
              <span key={it.name} className="flex items-center gap-2 text-xs text-white/70">
                <span className="w-3 h-3" style={{ background: it.color }} />
                {it.name}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* TASTING SHEET */}
      <section className="px-5 md:px-12 max-w-6xl mx-auto py-10 border-t border-white/5" data-testid="flight-tasting-sheet">
        <p className="overline text-[#C17F59] mb-2">Tasting Sheet</p>
        <h2 className="font-display text-4xl mb-7">Take notes. Argue politely.</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {flight.gins.map((g, i) => (
            <div key={g.id} className="border border-dashed border-white/15 p-5 min-h-[180px] relative bg-[#101113]" data-testid={`note-${g.id}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="font-display text-3xl text-white/70">0{i + 1}</span>
                <span className="font-hand text-xl text-[#D89A74]">{g.name}</span>
              </div>
              <div className="space-y-3 mt-3">
                {["Nose", "Palate", "Finish"].map((label) => (
                  <div key={label}>
                    <p className="overline text-white/40 mb-1">{label}</p>
                    <div className="h-px bg-white/10" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <p className="font-hand text-xl text-[#D89A74] mt-5">Pour, sip, write a single word per box.</p>
      </section>
    </div>
  );
}
